import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { BaggageType } from '@websky/graphql';
import { CartService, CartServicePayload, OnDeleteItemType } from '../../../Cart/types';
import CartItemOptionAdapter, {
	CartGroupOptionAdapterType
} from '../../../Cart/CartItemOptionAdapter/CartItemOptionAdapter';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import BaggageIcon from '../../../Cart/BaggageIcon/BaggageIcon';
import { ServiceSummaryType } from '../model/types';
import { ServiceOptionItem } from '../../../ServiceSummary/types';
import { removeDuplicates } from '@shared//lib/removeDuplicates';
import { useAggregatedSummaryServices } from '../lib/useAggregatedSummaryServices';
import { isShowInsurancesByPassengers } from '../../../ServiceSummary/utils';
import { CdnIcon, CdnIconNames } from '@shared/ui';
import { ALL_SEGMENTS_KEY } from '@shared/lib/flight';

interface Props {
	children?: (content: React.ReactNode) => React.ReactElement;
	renderSummary?: (groups: CartGroupOptionAdapterType[]) => React.ReactElement;
	serviceType: ServiceSummaryType;
	filter?: { param: 'id' | 'rfisc' | 'subType'; values: string[] };
	onDelete?: OnDeleteItemType;
	mode?: 'default' | 'aggregator';
	segmentId?: string;
}

const HEADER_ICON_MAP: Readonly<Partial<Record<ServiceSummaryType, CdnIconNames>>> = {
	Seat: 'Services/Seat Selection',
	Meal: 'Services/Meal',
	Animal: 'Baggage/Pet in cargo'
};

const CART_SERVICE_MAP: Readonly<Partial<Record<ServiceSummaryType, CartService>>> = {
	Seat: CartService.Seats,
	Baggage: CartService.Baggage,
	Animal: CartService.Baggage,
	Meal: CartService.Meal,
	BusinessLounge: CartService.BusinessLounge,
	Insurance: CartService.Insurances,
	Aeroexpress: CartService.Aeroexpress,
	Sms: CartService.SMS,
	Misc: CartService.Misc,
	Transfer: CartService.Transfer
};

const ServiceSummary: React.FC<Props> = ({
	children,
	renderSummary,
	serviceType,
	filter,
	onDelete,
	mode = 'default',
	segmentId
}) => {
	const aggregatedSummaryServices = useAggregatedSummaryServices();
	const summaryGroupsByType = aggregatedSummaryServices?.[serviceType] ?? [];

	const { isBaggageService, isInsuranceService } = useMemo(
		() => ({
			isBaggageService: serviceType === 'Baggage' || serviceType === 'Animal',
			isInsuranceService: serviceType === 'Insurance'
		}),
		[serviceType]
	);

	const onDeleteHandler = useCallback(
		(deleteMeta: CartServicePayload) => {
			onDelete?.(CART_SERVICE_MAP[serviceType], deleteMeta);
		},
		[onDelete, serviceType]
	);

	const processItems = useCallback(
		(items: ServiceOptionItem[] = []) => {
			let processedItems = [...items];

			if (segmentId) {
				processedItems = processedItems.filter(
					item =>
						item.segment === segmentId ||
						item.segment === ALL_SEGMENTS_KEY ||
						item.segmentIds?.includes(segmentId)
				);
			}

			if (filter) {
				processedItems = processedItems.filter(item => filter.values.includes(item[filter.param]));
			}

			if (isInsuranceService) {
				processedItems = isShowInsurancesByPassengers(mode)
					? removeDuplicates(processedItems, item => `${item.id}/${item.passengerName}`)
					: removeDuplicates(processedItems);
			}

			return processedItems.map(item => {
				let icon: React.ReactNode = null;

				if (isBaggageService && (item.iconName || item.rfisc)) {
					icon = <BaggageIcon baggageType={item.iconName as BaggageType} rfisc={item.rfisc} />;
				} else if (isInsuranceService) {
					icon = <CdnIcon icon={'Services/Transfer'} />;
				}

				return {
					...item,
					icon,
					onDelete: onDelete && item.deleteMeta ? () => onDeleteHandler(item.deleteMeta) : null
				};
			});
		},
		[mode, segmentId, filter, isBaggageService, isInsuranceService, onDelete, onDeleteHandler]
	);

	const groups = useMemo(() => {
		return summaryGroupsByType
			.map(group => ({
				...group,
				headerIcon: HEADER_ICON_MAP[serviceType] ? <CdnIcon icon={HEADER_ICON_MAP[serviceType]} /> : undefined,
				items: processItems(group.items)
			}))
			.filter(group => group.items?.length);
	}, [serviceType, summaryGroupsByType, processItems]);

	const content = useMemo(() => {
		if (!groups.length) {
			return null;
		}

		return (
			<ServiceSummaryDivider>
				{groups.map((group, index) => {
					const directionId = group.allSegments ? ALL_SEGMENTS_KEY : group.direction?.id;
					const travellerId = group.traveller?.id;
					const key = directionId && travellerId ? `${directionId}/${travellerId}` : index;

					return <CartItemOptionAdapter key={key} option={group} />;
				})}
			</ServiceSummaryDivider>
		);
	}, [groups]);

	if (renderSummary) {
		return renderSummary(groups);
	}

	return children?.(content) ?? null;
};

export default React.memo(ServiceSummary);
